/**
 * Kontora BFF – Workflow-Definitionen (Phase 4)
 *
 * Bewusst klein gehalten für die erste Iteration. Erweiterung pro Workflow
 * erfolgt durch Hinzufügen einer Section.
 *
 * Konvention:
 *   - code: kebab-case, eindeutig
 *   - context: in welchem Top-Level-Bereich der Workflow angeboten wird
 *   - requiredPermission: wenn vorhanden, braucht der User diese Permission
 *     sonst wird der Workflow ausgeblendet
 *   - steps.targetPath: Zielseite, an die am Ende weitergeleitet wird
 *     Die Zielseite kann über `?wf=<code>&step=N` Prefill-Logik machen
 */

import type { Workflow } from './workflow-types';
import type { NavigationContext } from '@/src/lib/navigation';

export const WORKFLOWS: readonly Workflow[] = [
  /* =====================================================================
   * WF-FIRST-INVOICE – Erste Rechnung erstellen
   * Zielgruppe: alle mit `invoice.create` (Operativ)
   * ===================================================================== */
  {
    code: 'wf-first-invoice',
    title: 'Erste Rechnung erstellen',
    description:
      'Wir führen dich Schritt für Schritt durch deine erste Rechnung – in unter 2 Minuten.',
    context: 'operations',
    requiredPermission: 'invoice.create',
    steps: [
      {
        id: 'has-customer',
        title: 'Hast du den Kunden schon angelegt?',
        description:
          'Wir brauchen einen Kunden, dem wir die Rechnung zuordnen können. Wenn du den Kunden noch nicht erfasst hast, hilft dir der manuelle Weg.',
        kind: 'choice',
        targetPath: '/verkauf/rechnungen/neu',
        choices: [
          {
            value: 'yes',
            label: 'Ja, ich wähle den Kunden gleich aus',
            description: 'Weiter zum nächsten Schritt.',
          },
          {
            value: 'no',
            label: 'Nein, ich lege einen neuen Kunden an',
            description: 'Wir leiten dich zur Stammdaten-Maske.',
          },
        ],
      },
      {
        id: 'customer-source',
        title: 'Woher kommen die Positionen?',
        description:
          'Du kannst Positionen aus deinem Produktkatalog übernehmen oder freie Texte erfassen.',
        kind: 'choice',
        targetPath: '/verkauf/rechnungen/neu',
        choices: [
          {
            value: 'catalog',
            label: 'Aus dem Produktkatalog',
            description: 'Wähle eines deiner hinterlegten Produkte – Preis und Steuer werden übernommen.',
          },
          {
            value: 'free',
            label: 'Freie Position',
            description: 'Du erfasst Beschreibung und Preis manuell – direkt auf der Rechnung.',
          },
          {
            value: 'mixed',
            label: 'Mix aus beidem',
            description: 'Manche Positionen aus dem Katalog, andere frei.',
          },
        ],
      },
      {
        id: 'options',
        title: 'Welche Optionen sollen gesetzt sein?',
        description:
          'Du kannst das später auf der Detailseite noch ändern – die Defaults sind sinnvoll für die meisten Fälle.',
        kind: 'choice',
        targetPath: '/verkauf/rechnungen/neu',
        choices: [
          {
            value: 'draft',
            label: 'Als Entwurf speichern',
            description: 'Standard – du prüfst die Rechnung vor dem Versand.',
          },
          {
            value: 'send',
            label: 'Direkt versenden (E-Mail)',
            description: 'Rechnung wird beim Speichern automatisch per E-Mail an den Kunden geschickt.',
          },
          {
            value: 'recurring',
            label: 'Als wiederkehrende Rechnung anlegen',
            description: 'Ideal für Abos oder monatliche Dienstleistungen.',
          },
        ],
      },
      {
        id: 'confirm',
        title: 'Bereit zum Anlegen?',
        description:
          'Klicke "Rechnung anlegen", um das Formular mit deinen Entscheidungen zu öffnen. Du kannst dort noch alles anpassen.',
        kind: 'confirm',
        targetPath: '/verkauf/rechnungen/neu',
      },
    ],
  },

  /* =====================================================================
   * WF-PROCESS-INCOMING – Ersten Eingangsbeleg verarbeiten
   * Zielgruppe: alle mit `purchase.read` (Operativ) oder `booking.create` (Buchhaltung)
   * ===================================================================== */
  {
    code: 'wf-process-incoming',
    title: 'Eingangsbeleg verarbeiten',
    description: 'Beleg hochladen, Lieferant zuordnen, buchen – in 3 Schritten.',
    context: 'operations',
    requiredPermission: 'purchase.read',
    steps: [
      {
        id: 'source',
        title: 'Wie liegt dir der Beleg vor?',
        description: 'Scan, PDF oder Foto – wir akzeptieren alle gängigen Formate.',
        kind: 'choice',
        targetPath: '/einkauf/eingangsbelege',
        choices: [
          {
            value: 'upload',
            label: 'Datei hochladen',
            description: 'Du lädst den Beleg direkt hoch.',
          },
          {
            value: 'email',
            label: 'Per E-Mail',
            description: 'Wir zeigen dir die Upload-Email-Adresse des Mandanten.',
          },
          {
            value: 'already',
            label: 'Ist schon im Archiv',
            description: 'Du suchst den Beleg im Dokumentenarchiv und verknüpfst ihn.',
          },
        ],
      },
      {
        id: 'supplier',
        title: 'Lieferant bekannt?',
        description:
          'Damit der Beleg korrekt zugeordnet wird, brauchen wir den Lieferanten – sonst als "Unbekannt" markieren.',
        kind: 'choice',
        targetPath: '/einkauf/eingangsbelege',
        choices: [
          {
            value: 'known',
            label: 'Ja, im Partnerstamm vorhanden',
            description: 'Du wählst den Lieferanten gleich aus.',
          },
          {
            value: 'new',
            label: 'Nein, muss angelegt werden',
            description: 'Du legst einen neuen Partner an.',
          },
          {
            value: 'unknown',
            label: 'Unbekannt (Sammelbeleg o.ä.)',
            description: 'Beleg wird ohne Lieferanten-Zuordnung verbucht.',
          },
        ],
      },
      {
        id: 'book-now',
        title: 'Soll der Beleg gleich gebucht werden?',
        description:
          'Mit "Sofort buchen" legen wir direkt eine Buchung an. Du kannst sie später noch anpassen.',
        kind: 'choice',
        targetPath: '/einkauf/eingangsbelege',
        choices: [
          {
            value: 'yes',
            label: 'Sofort buchen',
            description: 'Vorschlag wird direkt im Journal angelegt – du prüfst und gibst frei.',
          },
          {
            value: 'later',
            label: 'Später – nur Beleg erfassen',
            description: 'Beleg wartet auf Verarbeitung. Du buchst später mit einem Klick.',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * WF-INVITE-USER – Benutzer einladen
   * Zielgruppe: Admin+ (Mandant)
   * ===================================================================== */
  {
    code: 'wf-invite-user',
    title: 'Benutzer einladen',
    description: 'Lade einen neuen Benutzer in deinen Mandanten ein – mit passender Rolle.',
    context: 'tenant',
    requiredPermission: 'user.invite',
    steps: [
      {
        id: 'role',
        title: 'Welche Rolle soll der Benutzer bekommen?',
        description:
          'Die Rolle bestimmt, welche Bereiche und Aktionen der Benutzer sehen und ausführen darf.',
        kind: 'choice',
        targetPath: '/einstellungen/benutzer-rollen',
        choices: [
          {
            value: 'admin',
            label: 'Administrator',
            description: 'Verwaltet Benutzer und Stammdaten, hat aber keinen Zugriff auf System-Bereiche.',
          },
          {
            value: 'accountant',
            label: 'Buchhalter',
            description: 'Vollständige Buchführung ohne Benutzerverwaltung.',
          },
          {
            value: 'readonly',
            label: 'Lesezugriff',
            description: 'Sieht alles, kann aber nichts ändern.',
          },
        ],
      },
      {
        id: 'send-invite',
        title: 'Wie soll die Einladung verschickt werden?',
        description:
          'Wir senden eine E-Mail mit Anweisungen zum ersten Login. Das initiale Passwort wird vom System generiert.',
        kind: 'choice',
        targetPath: '/einstellungen/benutzer-rollen',
        choices: [
          {
            value: 'email',
            label: 'Per E-Mail an den Benutzer',
            description: 'Standard – der Benutzer erhält eine E-Mail mit Einladungslink.',
          },
          {
            value: 'manual',
            label: 'Selbst weitergeben',
            description: 'Wir zeigen dir die Login-Daten zum manuellen Weitergeben (z. B. persönliches Gespräch).',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * WF-BANK-IMPORT – Bankumsätze importieren
   * Zielgruppe: Buchhalter+ (Accounting)
   * ===================================================================== */
  {
    code: 'wf-bank-import',
    title: 'Bankumsätze importieren',
    description:
      'CSV-Datei hochladen, Transaktionen zuordnen, bestätigen – fertig.',
    context: 'accounting',
    requiredPermission: 'bank_statement.import',
    steps: [
      {
        id: 'profile',
        title: 'Welches Bankprofil verwendest du?',
        description:
          'Wenn du schon ein Profil angelegt hast (z. B. "Sparkasse Geschäftskonto"), wähle es hier. Sonst erstellen wir ein neues.',
        kind: 'choice',
        targetPath: '/importe',
        choices: [
          {
            value: 'existing',
            label: 'Bestehendes Profil verwenden',
            description: 'Du wählst aus deinen vorhandenen Importprofilen.',
          },
          {
            value: 'new',
            label: 'Neues Profil anlegen',
            description: 'Wir helfen dir, ein Profil für deine Bank anzulegen.',
          },
        ],
      },
      {
        id: 'matching',
        title: 'Wie sollen Transaktionen zugeordnet werden?',
        description:
          'Die meisten Buchungen erkennt das System automatisch – du kannst das Verhalten feinjustieren.',
        kind: 'choice',
        targetPath: '/importe',
        choices: [
          {
            value: 'auto',
            label: 'Vollautomatisch zuordnen',
            description: 'Buchungsvorschläge direkt im Journal – du prüfst nur die Ausnahmen.',
          },
          {
            value: 'manual',
            label: 'Manuell zuordnen',
            description: 'Du gehst jeden Umsatz einzeln durch und ordnest ihn selbst zu.',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * WF-MONTHLY-CLOSE – Monatsabschluss durchführen
   * Zielgruppe: Buchhalter+ (Accounting) – DER Hauptprozess am Monatsende
   * ===================================================================== */
  {
    code: 'wf-monthly-close',
    title: 'Monatsabschluss durchführen',
    description:
      'OP-Liste prüfen, Bank abstimmen, Steuern prüfen, Reports generieren, Periode schließen – in 5 Schritten.',
    context: 'accounting',
    requiredPermission: 'report.read',
    steps: [
      {
        id: 'open-items',
        title: 'Wie ist der Stand deiner offenen Posten?',
        description:
          'Offene Posten, die noch nicht ausgeglichen sind, müssen vor dem Abschluss geklärt oder begründet werden.',
        kind: 'choice',
        targetPath: '/buchhaltung/offene-posten',
        choices: [
          {
            value: 'clean',
            label: 'Alle OP sind ausgeglichen oder begründet',
            description: 'Wir können direkt mit dem nächsten Schritt weitermachen.',
          },
          {
            value: 'review',
            label: 'Einige müssen noch bearbeitet werden',
            description: 'Wir leiten dich zur OP-Liste – du kehrst danach zurück.',
          },
          {
            value: 'many',
            label: 'Viele unbegründete OP',
            description: 'Wir empfehlen zuerst die OP-Liste durchzugehen und Mahnläufe zu starten.',
          },
        ],
      },
      {
        id: 'bank-reconciliation',
        title: 'Sind deine Bankkonten abgestimmt?',
        description:
          'Vor dem Abschluss sollten alle Bankbewegungen einem Konto zugeordnet sein. Andernfalls entstehen Lücken im Journal.',
        kind: 'choice',
        targetPath: '/buchhaltung/zahlungsabgleich',
        choices: [
          {
            value: 'yes',
            label: 'Ja, alle Konten sind abgestimmt',
            description: 'Wir können mit dem nächsten Schritt weitermachen.',
          },
          {
            value: 'no',
            label: 'Nein, einige sind offen',
            description: 'Wir leiten dich zum Zahlungsabgleich – du kehrst danach zurück.',
          },
          {
            value: 'unsure',
            label: 'Ich bin nicht sicher',
            description: 'Wir zeigen dir den aktuellen Stand der Abstimmung.',
          },
        ],
      },
      {
        id: 'tax-check',
        title: 'Hast du die Steuern geprüft?',
        description:
          'Steuerliche Nachweise und blockierte Fälle sollten vor dem Abschluss geklärt sein.',
        kind: 'choice',
        targetPath: '/steuern/pruefungen',
        choices: [
          {
            value: 'yes',
            label: 'Ja, alles ist geprüft',
            description: 'Wir können weiter zu den Reports.',
          },
          {
            value: 'no',
            label: 'Nein, muss ich noch machen',
            description: 'Wir leiten dich zur Steuer-Prüfung – du kehrst danach zurück.',
          },
          {
            value: 'skip',
            label: 'Überspringen – diese Periode ist steuerfrei',
            description: 'Achtung: Das ist nur in Sonderfällen sinnvoll (z. B. Kleinunternehmer).',
          },
        ],
      },
      {
        id: 'reports',
        title: 'Welche Reports möchtest du generieren?',
        description:
          'Du kannst mehrere Reports gleichzeitig erstellen. Wir legen sie als Snapshots ab und sie sind audit-fest.',
        kind: 'choice',
        targetPath: '/reports/monthly-close',
        choices: [
          {
            value: 'standard',
            label: 'Standard-Set (EUER, BWA, OPOS)',
            description: 'Die wichtigsten drei Reports für einen typischen Monatsabschluss.',
          },
          {
            value: 'full',
            label: 'Komplett-Set',
            description: 'Alle 8 Standard-Reports inkl. Cashflow, Profitability, Tax Risk.',
          },
          {
            value: 'minimal',
            label: 'Nur EUER',
            description: 'Minimaler Abschluss, wenn du nur die Steuerbilanz brauchst.',
          },
        ],
      },
      {
        id: 'close-period',
        title: 'Periode jetzt schließen?',
        description:
          'Eine geschlossene Periode kann nur noch von Tenant Admins oder Sysadmins geöffnet werden. Stelle sicher, dass alle Schritte erledigt sind.',
        kind: 'choice',
        targetPath: '/buchhaltung/perioden',
        choices: [
          {
            value: 'close',
            label: 'Ja, Periode jetzt schließen',
            description: 'Aktion ist nach Bestätigung durch Tenant Admin nicht mehr umkehrbar.',
          },
          {
            value: 'later',
            label: 'Später – Reports als Entwurf lassen',
            description: 'Du kannst den Abschluss später fortsetzen – die Daten bleiben gespeichert.',
          },
          {
            value: 'admin',
            label: 'Admin soll schließen',
            description: 'Wir benachrichtigen den Tenant Admin, dass die Periode bereit zum Schließen ist.',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * WF-INVITE-ADVISOR – Steuerberater einladen
   * Zielgruppe: Mandant Admin+
   * ===================================================================== */
  {
    code: 'wf-invite-advisor',
    title: 'Steuerberater einladen',
    description:
      'Kanzlei-Zugang einrichten, Aufgaben definieren, Zugriffs-Scope festlegen.',
    context: 'tenant',
    requiredPermission: 'advisor.access.manage',
    steps: [
      {
        id: 'advisor',
        title: 'Welche Kanzlei willst du einladen?',
        description:
          'Du kannst zwischen einer bestehenden Kanzlei wählen oder eine neue anlegen.',
        kind: 'choice',
        targetPath: '/steuerberater',
        choices: [
          {
            value: 'existing',
            label: 'Bestehende Kanzlei im Mandant',
            description: 'Du wählst eine Kanzlei aus deinen bestehenden Einträgen.',
          },
          {
            value: 'new',
            label: 'Neue Kanzlei anlegen',
            description: 'Wir helfen dir, eine neue Kanzlei mit Stammdaten anzulegen.',
          },
        ],
      },
      {
        id: 'scope',
        title: 'Welchen Zugriff bekommt die Kanzlei?',
        description:
          'Du kannst später jederzeit ändern – die Kanzlei bekommt nur das, was du hier freischaltest.',
        kind: 'choice',
        targetPath: '/steuerberater/zugriff',
        choices: [
          {
            value: 'read-only',
            label: 'Nur lesen',
            description: 'Kanzlei kann Belege und Buchungen einsehen, aber nicht ändern.',
          },
          {
            value: 'read-comments',
            label: 'Lesen + Kommentare',
            description: 'Zusätzlich kann die Kanzlei Kommentare und Reviews hinterlassen.',
          },
          {
            value: 'read-prepare',
            label: 'Lesen + vorbereiten',
            description: 'Kanzlei kann Buchungen vorbereiten (ELSTER-Entwürfe), du musst freigeben.',
          },
        ],
      },
      {
        id: 'tasks',
        title: 'Welche Aufgaben soll die Kanzlei übernehmen?',
        description:
          'Du kannst vordefinierte Aufgaben zuweisen oder später selbst welche anlegen.',
        kind: 'choice',
        targetPath: '/steuerberater/aufgaben',
        choices: [
          {
            value: 'monthly-vat',
            label: 'Monatliche UStVA',
            description: 'Kanzlei erstellt und prüft die monatliche UStVA-Voranmeldung.',
          },
          {
            value: 'yearly-returns',
            label: 'Jahresabschluss',
            description: 'Kanzlei übernimmt den kompletten Jahresabschluss inkl. Steuererklärungen.',
          },
          {
            value: 'monitoring',
            label: 'Laufende Beobachtung',
            description: 'Kanzlei prüft regelmäßig auf Auffälligkeiten und benachrichtigt dich.',
          },
        ],
      },
      {
        id: 'invite',
        title: 'Einladung verschicken?',
        description:
          'Die Kanzlei erhält eine E-Mail mit Anweisungen zur ersten Anmeldung.',
        kind: 'choice',
        targetPath: '/steuerberater/zugriff',
        choices: [
          {
            value: 'email',
            label: 'Ja, per E-Mail einladen',
            description: 'Standard – die Kanzlei erhält eine Einladung mit temporärem Zugangscode.',
          },
          {
            value: 'manual',
            label: 'Später – Daten manuell weitergeben',
            description: 'Wir zeigen dir die Zugangsdaten zum persönlichen Weitergeben.',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * WF-SYSTEM-BACKUP – Backup prüfen
   * Zielgruppe: Sysadmin
   * ===================================================================== */
  {
    code: 'wf-system-backup',
    title: 'System-Backup prüfen',
    description:
      'Restore-Test, Backup-Zeitplan, Monitoring – die drei wichtigsten Checks für dein Backup.',
    context: 'system',
    requiredPermission: 'system.scheduler.read',
    steps: [
      {
        id: 'schedule',
        title: 'Läuft dein Backup-Zeitplan wie erwartet?',
        description:
          'Standard: Tägliches Vollbackup + wöchentliches differentielles Backup. Außerhalb der Norm solltest du eingreifen.',
        kind: 'choice',
        targetPath: '/system/scheduler',
        choices: [
          {
            value: 'ok',
            label: 'Ja, läuft planmäßig',
            description: 'Wir überspringen den Schedule-Check.',
          },
          {
            value: 'review',
            label: 'Nicht sicher – bitte zeigen',
            description: 'Wir leiten dich zur Scheduler-Übersicht.',
          },
          {
            value: 'broken',
            label: 'Nein, Backup läuft nicht',
            description: 'Wir leiten dich zur Scheduler-Verwaltung – bitte prüfe die letzten Logs.',
          },
        ],
      },
      {
        id: 'restore-test',
        title: 'Wann hattest du den letzten Restore-Test?',
        description:
          'Ein Backup ist nur so gut wie sein Restore-Test. Wir empfehlen mindestens einen Test pro Quartal.',
        kind: 'choice',
        targetPath: '/system/jobs',
        choices: [
          {
            value: 'recent',
            label: 'In den letzten 30 Tagen',
            description: 'Wir überspringen den Restore-Test-Check.',
          },
          {
            value: 'old',
            label: 'Länger als 30 Tage her',
            description: 'Wir empfehlen, einen Restore-Test einzuplanen.',
          },
          {
            value: 'never',
            label: 'Noch nie',
            description: 'Achtung: Du hast aktuell keinen Beleg, dass deine Backups funktionieren.',
          },
        ],
      },
      {
        id: 'monitoring',
        title: 'Ist dein Monitoring aktiv?',
        description:
          'Du solltest benachrichtigt werden, wenn ein Backup fehlschlägt oder eine Recovery zu lange dauert.',
        kind: 'choice',
        targetPath: '/system/logs',
        choices: [
          {
            value: 'alerts',
            label: 'Ja, Alerts sind konfiguriert',
            description: 'Wir empfehlen, die Alert-Schwellen halbjährlich zu prüfen.',
          },
          {
            value: 'partial',
            label: 'Teilweise – nur E-Mail',
            description: 'Du verpasst Alarme, wenn du offline bist. Wir empfehlen zusätzlich Pager.',
          },
          {
            value: 'none',
            label: 'Kein Monitoring',
            description: 'Wir empfehlen dringend, mindestens E-Mail-Alerts einzurichten.',
          },
        ],
      },
    ],
  },
];

export const WORKFLOW_CODES = WORKFLOWS.map((w) => w.code);

export function findWorkflow(code: string): Workflow | undefined {
  return WORKFLOWS.find((w) => w.code === code);
}

export function workflowsForContext(
  context: NavigationContext,
  permissions: readonly string[],
): Workflow[] {
  return WORKFLOWS.filter((w) => {
    if (w.context !== context) return false;
    if (!w.requiredPermission) return true;
    return permissions.includes(w.requiredPermission) || permissions.includes('*');
  });
}